import { useState, useRef } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  type WidgetInstanceId,
  DASHBOARD_WIDGETS,
  DASHBOARD_GRID_COLS,
  getBaseWidgetId,
  isFilterableWidget,
  isOpenPositionFilterableWidget,
} from '../config/dashboardWidgets'

/** Full class names so Tailwind JIT includes them. Key: mobileWidth-desktopWidth */
const COL_SPAN_CLASSES: Record<string, string> = {
  '1-1': 'col-span-1 md:col-span-1',
  '1-2': 'col-span-1 md:col-span-2',
  '1-3': 'col-span-1 md:col-span-3',
  '1-4': 'col-span-1 md:col-span-4',
  '2-1': 'col-span-2 md:col-span-1',
  '2-2': 'col-span-2 md:col-span-2',
  '2-3': 'col-span-2 md:col-span-3',
  '2-4': 'col-span-2 md:col-span-4',
}

const ROW_SPAN_CLASSES: Record<number, string> = {
  1: 'row-span-1',
  2: 'row-span-2',
  3: 'row-span-3',
  4: 'row-span-4',
  5: 'row-span-5',
  6: 'row-span-6',
  7: 'row-span-7',
  8: 'row-span-8',
}
import { useDashboardLayout } from '../contexts/DashboardLayoutContext'
import WidgetFilterPopover from './WidgetFilterPopover'

interface Strategy {
  id: string
  display_name: string
}

interface WidgetAccount {
  id: string
  name: string
  platform: string
  currency: string
}

interface EditableDashboardWidgetsProps {
  enabledOrder: WidgetInstanceId[]
  isEditMode: boolean
  onReorder: (newOrder: WidgetInstanceId[]) => void
  onRemove: (id: WidgetInstanceId) => void
  children: (id: WidgetInstanceId) => React.ReactNode
  strategies?: Strategy[]
  trades?: { exchange?: string | null; tags?: string[] | null }[]
  accounts?: WidgetAccount[]
}

function SortableWidgetWrapper({
  id,
  isEditMode,
  onRemove,
  width,
  height,
  mobileWidth,
  children,
  strategies = [],
  trades = [],
  accounts = [],
}: {
  id: WidgetInstanceId
  isEditMode: boolean
  onRemove: (id: WidgetInstanceId) => void
  width: number
  height: number
  mobileWidth: number
  children: React.ReactNode
  strategies: Strategy[]
  trades: { exchange?: string | null; tags?: string[] | null }[]
  accounts: WidgetAccount[]
}) {
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const { getWidgetSettings, updateWidgetSettings } = useDashboardLayout()
  const showFilterButton = isFilterableWidget(id)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const colSpanClass = COL_SPAN_CLASSES[`${mobileWidth}-${width}`] ?? 'col-span-1 md:col-span-1'
  const rowSpanClass = ROW_SPAN_CLASSES[height] ?? 'row-span-1'

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative min-h-0 min-w-0 flex flex-col overflow-hidden ${colSpanClass} ${rowSpanClass} ${isDragging ? 'z-10 opacity-90' : ''}`}
    >
      {isEditMode && (
        <div className="absolute top-2 right-2 z-20 flex items-center gap-1 flex-shrink-0">
          {showFilterButton && (
            <div className="relative">
              <button
                ref={filterButtonRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setFilterPopoverOpen((v) => !v)
                }}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm"
                aria-label="Edit widget filters"
              >
                ⋯
              </button>
              <WidgetFilterPopover
                anchorRef={filterButtonRef}
                isOpen={filterPopoverOpen}
                onClose={() => setFilterPopoverOpen(false)}
                settings={getWidgetSettings(id)}
                onSave={(s) => updateWidgetSettings(id, s)}
                strategies={strategies}
                trades={trades}
                accounts={accounts}
                hiddenFilters={
                  isOpenPositionFilterableWidget(id)
                    ? ['dateRange', 'strategy']
                    : ['account', 'tag']
                }
              />
            </div>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onRemove(id)
            }}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 text-sm font-bold shadow-md"
            aria-label="Remove widget"
          >
            −
          </button>
        </div>
      )}
      {isEditMode ? (
        <div
          className="flex-1 min-h-0 overflow-hidden cursor-grab active:cursor-grabbing touch-none"
          {...attributes}
          {...listeners}
        >
          {children}
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden">{children}</div>
      )}
    </div>
  )
}

export default function EditableDashboardWidgets({
  enabledOrder,
  isEditMode,
  onReorder,
  onRemove,
  children,
  strategies = [],
  trades = [],
  accounts = [],
}: EditableDashboardWidgetsProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = enabledOrder.indexOf(active.id as WidgetInstanceId)
    const newIndex = enabledOrder.indexOf(over.id as WidgetInstanceId)
    if (oldIndex === -1 || newIndex === -1) return
    onReorder(arrayMove(enabledOrder, oldIndex, newIndex))
  }

  const gridClassName = 'grid grid-cols-1 md:grid-cols-4 grid-auto-rows-[minmax(44px,auto)] md:grid-rows-[repeat(8,minmax(0,1fr))] md:grid-auto-rows-auto grid-flow-dense gap-2 min-h-0 min-w-0 md:h-full'

  const mobileSpan = () => 1

  if (isEditMode) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={enabledOrder}
          strategy={verticalListSortingStrategy}
        >
          <div className={`${gridClassName} overflow-x-hidden md:overflow-y-auto`}>
            {enabledOrder.map((id) => {
              const baseId = getBaseWidgetId(id)
              const config = DASHBOARD_WIDGETS[baseId as keyof typeof DASHBOARD_WIDGETS]
              const w = config?.width ?? 1
              const h = config?.height ?? 1
              return (
                <SortableWidgetWrapper
                  key={id}
                  id={id}
                  isEditMode={isEditMode}
                  onRemove={onRemove}
                  width={w}
                  height={h}
                  mobileWidth={mobileSpan(w)}
                  strategies={strategies}
                  trades={trades}
                  accounts={accounts}
                >
                  {children(id)}
                </SortableWidgetWrapper>
              )
            })}
          </div>
        </SortableContext>
      </DndContext>
    )
  }

  return (
    <div className={`${gridClassName} overflow-x-hidden md:overflow-y-auto`}>
      {enabledOrder.map((id) => {
        const baseId = getBaseWidgetId(id)
        const config = DASHBOARD_WIDGETS[baseId as keyof typeof DASHBOARD_WIDGETS]
        const w = config?.width ?? 1
        const h = config?.height ?? 1
        const mw = mobileSpan(w)
        const colClass = COL_SPAN_CLASSES[`${mw}-${w}`] ?? 'col-span-1 md:col-span-1'
        const rowClass = ROW_SPAN_CLASSES[h] ?? 'row-span-1'
        return (
          <div
            key={id}
            className={`relative min-h-0 min-w-0 flex flex-col overflow-hidden ${colClass} ${rowClass}`}
          >
            <div className="flex-1 min-h-0 overflow-hidden">{children(id)}</div>
          </div>
        )
      })}
    </div>
  )
}
